import { useState, useEffect, useCallback } from 'react';
import {
  Bot,
  Play,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  AlertCircle,
} from 'lucide-react';
import { agentApi, reportsApi } from '@/services/api';
import { clsx } from 'clsx';

interface AgentRun {
  id: string;
  type: 'query' | 'report' | 'cross-persona';
  input: string;
  status: 'running' | 'completed' | 'failed';
  result?: string;
  error?: string;
  startedAt: number;
  completedAt?: number;
}

export function AgentsPage() {
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysisTypes, setAnalysisTypes] = useState<string[]>([]);

  useEffect(() => {
    reportsApi
      .analysisTypes()
      .then(({ types }) => setAnalysisTypes(types))
      .catch(() => {});
  }, []);

  const runAgent = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!query.trim()) return;

      const run: AgentRun = {
        id: `run-${Date.now()}`,
        type: 'query',
        input: query.trim(),
        status: 'running',
        startedAt: Date.now(),
      };

      setRuns((prev) => [run, ...prev]);
      setQuery('');
      setLoading(true);

      try {
        const result = await agentApi.query({ query: run.input });
        setRuns((prev) =>
          prev.map((r) =>
            r.id === run.id
              ? {
                  ...r,
                  status: 'completed' as const,
                  result: JSON.stringify(result, null, 2),
                  completedAt: Date.now(),
                }
              : r,
          ),
        );
      } catch (err) {
        setRuns((prev) =>
          prev.map((r) =>
            r.id === run.id
              ? {
                  ...r,
                  status: 'failed' as const,
                  error: err instanceof Error ? err.message : 'Failed',
                  completedAt: Date.now(),
                }
              : r,
          ),
        );
      } finally {
        setLoading(false);
      }
    },
    [query],
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Agent Workflows</h1>
        <p className="text-sm text-slate-500 mt-1">
          Interact with the research agent, trigger report generation, and
          monitor running workflows.
        </p>
      </div>

      {/* Agent query */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h2 className="font-semibold text-navy-900 text-sm mb-4 flex items-center gap-2">
          <Bot size={16} className="text-brand-teal" />
          Agent Query
        </h2>
        <form onSubmit={runAgent} className="flex gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask the research agent..."
            className="flex-1 px-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-teal/30 focus:border-brand-teal"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-navy-900 font-semibold text-sm rounded-lg hover:bg-brand-teal/90 disabled:opacity-40 transition-colors"
          >
            {loading ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
            Run
          </button>
        </form>
      </div>

      {/* Report types */}
      {analysisTypes.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h2 className="font-semibold text-navy-900 text-sm mb-3">
            Available Analysis Types
          </h2>
          <div className="flex flex-wrap gap-2">
            {analysisTypes.map((t) => (
              <span
                key={t}
                className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full border border-slate-200 font-medium"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Run history */}
      <div>
        <h2 className="font-semibold text-navy-900 text-sm mb-3">
          Run History
          {runs.length > 0 && (
            <span className="ml-2 text-xs text-slate-400 font-normal">
              ({runs.length})
            </span>
          )}
        </h2>

        {runs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Bot size={32} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">
              No agent runs yet. Send a query above.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {runs.map((run) => (
              <RunCard key={run.id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunCard({ run }: { run: AgentRun }) {
  const [expanded, setExpanded] = useState(false);

  const statusConfig = {
    running: { icon: Loader2, color: 'text-blue-600', bg: 'bg-blue-50' },
    completed: {
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  };

  const cfg = statusConfig[run.status];
  const Icon = cfg.icon;
  const duration = run.completedAt
    ? `${((run.completedAt - run.startedAt) / 1000).toFixed(1)}s`
    : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={clsx(
              'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
              cfg.bg,
              cfg.color,
            )}
          >
            <Icon
              size={16}
              className={clsx(run.status === 'running' && 'animate-spin')}
            />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-navy-900 truncate">
              {run.input}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] uppercase font-bold text-slate-400">
                {run.type}
              </span>
              {duration && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <Clock size={10} /> {duration}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <span
          className={clsx(
            'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0',
            cfg.bg,
            cfg.color,
          )}
        >
          {run.status}
        </span>
      </div>

      {expanded && (run.result || run.error) && (
        <div className="border-t border-slate-100 px-4 py-3 bg-slate-50">
          {run.error ? (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle size={14} />
              {run.error}
            </div>
          ) : (
            <pre className="text-xs text-slate-700 font-mono overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
              {run.result}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
